import clsx from 'clsx';
import {useFlags} from 'launchdarkly-react-client-sdk';
import React from 'react';
import {Paper, Stack} from '@mui/material';
import {Icon, Tooltip} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {CreatorBrandSafetyVettingStatus} from '@/types/creatorBrandSafetyVetting';
import {SocialNetworkInfo} from '@/types/socials';
import Checkbox from '@/components/checkbox';
import Badge from '@/components/creator-card/Badge';
import CreatorAvatar from '@/components/creator-card/CreatorAvatar';
import CreatorCardCta from '@/components/creator-card/CreatorCardCta';
import CreatorCategories from '@/components/creator-card/CreatorCategories';
import CreatorInfo from '@/components/creator-card/CreatorInfo';
import CreatorOverflowMenuButton, {
  CreatorMenuItemProps,
} from '@/components/creator-card/CreatorOverflowMenuButton';
import CreatorSocialInfo from '@/components/creator-card/CreatorSocialInfo';
import CreatorSocialsIcons from '@/components/creator-card/CreatorSocialsIcons';
import ImagesStack from '@/components/creator-card/ImagesStack';
import Link from '@/components/link';
import SafeCollabChip from '@/components/safe-collab-chip';
import useNavigateToLatestReport from '@/hooks/use-navigate-to-latest-report';
import styles from './CreatorCard.module.scss';

const CREATOR_OVERFLOW_MENU_STYLES = {
  container: styles.overflowMenu,
  button: styles.overflowMenuButton,
};

export type CreatorCardProps = {
  testID?: string;
  id: string;
  fullName: string;
  handle?: string | null;
  age?: string;
  location?: string;
  profileImage?: string;
  categories: string[];
  images: [string, string, string];
  // email is used when CreatorCardProps is passed in onClick event
  // eslint-disable-next-line react/no-unused-prop-types
  email: string;
  socialNetworks: SocialNetworkInfo[];
  selectedSocialNetworkProvider?: string;
  rating?: number;
  signupDate?: string;
  creatorCardCtaProps?: CreatorCardCtaProps;
  creatorProfileUrl?: string;
  isOverflowMenuEnabled?: boolean;
  creatorCardOverflowMenuItemsCallBack?: (
    creator: CreatorCardProps
  ) => Promise<CreatorMenuItemProps[]>;
  onSocialNetworkClick?: (networkProvider: string, creatorId: string) => void;
  onCardClick?: (creatorId: string) => void;
  isSelectMode?: boolean;
  isSelected?: boolean;
  onSelectChange?: (id: string, selected: boolean) => void;
  vettingDecision?: CreatorBrandSafetyVettingStatus;
  isShortlisted?: boolean;
  hideSocialIcons?: boolean;
  // Renders a green checkmark badge on the avatar when the creator's gig is done.
  isGigCompleted?: boolean;
  /** Softer card highlight for the most recently viewed creator. */
  isLastViewed?: boolean;
  /** Shows a draft affordance when a local message draft exists. */
  hasDraft?: boolean;
  /** Extra icon(s) rendered beside the creator name (e.g. application expiration). */
  extraNameAdornment?: React.ReactNode;
  source?: string;
};

export type CreatorCardCtaProps = {
  buttonText: (creator: CreatorCardProps) => string | React.JSX.Element;
  onClick: (event: React.MouseEvent, creator: CreatorCardProps) => void;
  isDisabled: (creator: CreatorCardProps) => boolean | undefined;
};

function CreatorCard(props: CreatorCardProps) {
  const flags = useFlags();
  const {
    testID = 'creator-card',
    id,
    socialNetworks,
    selectedSocialNetworkProvider,
    images,
    profileImage,
    fullName,
    handle,
    age,
    location,
    categories,
    rating,
    signupDate,
    creatorCardCtaProps,
    creatorProfileUrl,
    isOverflowMenuEnabled,
    creatorCardOverflowMenuItemsCallBack = async () => [],
    onSocialNetworkClick,
    onCardClick,
    isSelectMode,
    isSelected,
    onSelectChange,
    vettingDecision,
    isShortlisted,
    hideSocialIcons = false,
    isGigCompleted = false,
    isLastViewed = false,
    hasDraft = false,
    extraNameAdornment,
    source,
  } = props;
  const isOffPlatform = source === 'off_platform';
  const leadPlatformInfo = socialNetworks.find((network) =>
    selectedSocialNetworkProvider
      ? network.provider.includes(selectedSocialNetworkProvider)
      : undefined
  );

  const handleSafeCollabClick = useNavigateToLatestReport(
    id,
    flags.fifaRevamp ? 'agency' : undefined,
    'crm'
  );

  function renderCardCta() {
    if (!creatorCardCtaProps) {
      return null;
    }
    return (
      <CreatorCardCta
        buttonText={creatorCardCtaProps.buttonText(props)}
        onClick={(event: React.MouseEvent) => {
          creatorCardCtaProps.onClick(event, props);
        }}
        isDisabled={creatorCardCtaProps.isDisabled(props)}
      />
    );
  }

  const creatorCardElement = (
    <Paper
      elevation={1}
      square={false}
      className={clsx(
        styles.container,
        isSelected && styles.selected,
        isLastViewed && !isSelected && styles.lastViewed
      )}
      data-testid={testID}
    >
      {isSelectMode && onSelectChange ? (
        <div
          className={styles.checkboxOverlay}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onSelectChange(id, !isSelected);
          }}
        >
          <Checkbox
            className={styles.checkboxContainer}
            variant="light"
            checked={!!isSelected}
            onChange={() => {}}
          />
        </div>
      ) : null}
      <CreatorOverflowMenuButton
        isEnabled={isOverflowMenuEnabled && !isSelectMode}
        classes={CREATOR_OVERFLOW_MENU_STYLES}
        creator={props}
        creatorCardOverflowMenuItemsCallBack={
          creatorCardOverflowMenuItemsCallBack
        }
      />
      <Badge
        rating={rating}
        signupDate={signupDate}
        isShortlisted={isShortlisted}
      />
      <ImagesStack images={images} />
      <CreatorAvatar
        profileImageUrl={profileImage}
        isGigCompleted={isGigCompleted}
        isOffPlatform={isOffPlatform}
      />
      <Stack direction="column" className={styles.contentStack}>
        <Stack
          direction="column"
          spacing="8px"
          alignItems="center"
          className={styles.headerSection}
        >
          <CreatorInfo
            fullName={fullName}
            handle={handle}
            age={age}
            location={location}
            nameAdornment={
              extraNameAdornment || hasDraft ? (
                <>
                  {extraNameAdornment}
                  {hasDraft ? (
                    <Tooltip title={translate('Draft')} arrow placement="top">
                      <span
                        data-testid="message-draft-icon"
                        role="img"
                        aria-label={translate('Draft')}
                        className={styles.draftIcon}
                      >
                        <Icon
                          name="Navigation-Edit"
                          size="small"
                          appearance="secondary"
                        />
                      </span>
                    </Tooltip>
                  ) : null}
                </>
              ) : null
            }
          />
          <CreatorCategories categories={categories} />
          {vettingDecision && !isOffPlatform ? (
            <SafeCollabChip
              status={vettingDecision}
              onClick={handleSafeCollabClick}
            />
          ) : null}
        </Stack>
        <Stack direction="column" spacing="8px" alignItems="center">
          {!hideSocialIcons && (
            <CreatorSocialsIcons
              socialNetworks={socialNetworks}
              selectedPlatformProvider={selectedSocialNetworkProvider}
              onClickSocialNetwork={(networkProvider: string) =>
                onSocialNetworkClick?.(networkProvider, id)
              }
            />
          )}
          {leadPlatformInfo && (
            <CreatorSocialInfo leadPlatformInfo={leadPlatformInfo} />
          )}
        </Stack>
        {renderCardCta()}
      </Stack>
    </Paper>
  );
  return creatorProfileUrl ? (
    <Link to={creatorProfileUrl} onClick={() => onCardClick?.(id)}>
      {creatorCardElement}
    </Link>
  ) : (
    creatorCardElement
  );
}

export default CreatorCard;
